import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
    AlertTriangle,
    Armchair,
    Building2,
    CalendarDays,
    ChevronDown,
    Clock,
    FileDown,
    FileSpreadsheet,
    IndianRupee,
    Lock,
    ReceiptText,
    RefreshCw,
    Users,
} from 'lucide-react';
import ScreenHeader from '../components/layout/ScreenHeader';
import {
    Badge,
    Button,
    Card,
    EmptyState,
    FilterCard,
    FilterToggle,
    IconButton,
    Notice,
    Pagination,
    SelectInput,
    StatCard,
    StatGrid,
    StatusPill,
    TableSkeleton,
    Tabs,
    TextInput,
} from '../ui';
import { PlayerInvoiceDialog, TableInvoiceDialog } from '../components/TableInvoice';
import { exportToExcel, exportToPDF } from '../utils/exportUtils';
import { formatMinutes, formatRupees } from '../utils/timeFormat';

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';
if (API_URL.endsWith('/api')) {
    API_URL = API_URL.slice(0, -4);
}
if (API_URL.endsWith('/')) {
    API_URL = API_URL.slice(0, -1);
}

const REFRESH_MS = 30 * 1000;
const fmtTime = (value) => (value ? moment(value).format('DD MMM, hh:mm A') : '-');
// The most recent run carries the true last-started/last-stopped times; the table's
// live runningSince/lastStoppedAt snapshot is only a fallback for tables with no runs
// in the selected date range.
const latestRunOf = (t) => (t.runs && t.runs[0]) || null;

// Seated anywhere right now (isSeated comes from the server and ignores the report
// filters); open sessions in the report are a fallback
const isPlayerSeated = (player) =>
    Boolean(player.isSeated || player.currentTable || player.sessions.some((s) => !s.endedAt));

const TH = 'px-3 py-2.5 text-left text-xs font-semibold text-cm-muted whitespace-nowrap';
const TD = 'px-3 py-3 align-top';

const PrivateChip = ({ booking }) => (
    <span
        title={
            booking
                ? `Booked private ${fmtTime(booking.startedAt)} to ${fmtTime(booking.endedAt || booking.until)}`
                : 'Booked private'
        }
    >
        <StatusPill tone="warning" icon={Lock} size="sm">Private</StatusPill>
    </span>
);

const InvoiceButton = ({ onClick, disabled, title = 'Generate invoice' }) => (
    <IconButton icon={ReceiptText} label={title} variant="outline" size="sm" onClick={onClick} disabled={disabled} />
);

// Bill status of a closed stay; null for open stays and pre-billing history
const PaymentChip = ({ status, method, markedUnpaid }) => {
    if (!status) return <span className="text-cm-faint">—</span>;
    const paid = status === 'paid';
    return (
        <StatusPill tone={paid ? 'success' : markedUnpaid ? 'warning' : 'danger'} size="sm">
            {paid ? `Paid${method === 'cash' ? ' · Cash' : ''}` : markedUnpaid ? 'Marked as unpaid' : 'Unpaid'}
        </StatusPill>
    );
};

// A table row that opens a nested detail table underneath
const RowWithDetails = ({ cells, detailHeaders, detailRows, colSpan, serialNumber }) => {
    const [open, setOpen] = useState(false);
    const rows = open ? detailRows() : null;
    return (
        <>
            <tr className={`border-t border-cm-border hover:bg-cm-surface-2 ${open ? 'bg-cm-surface-2' : ''}`}>
                <td className={`${TD} w-10`}>
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        aria-label={open ? 'Hide details' : 'Show details'}
                        className="grid h-7 w-7 place-items-center rounded-cm text-cm-muted hover:bg-cm-surface-3 hover:text-cm-text"
                    >
                        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                </td>
                <td className={`${TD} text-cm-muted tabular-nums`}>{serialNumber}</td>
                {cells}
            </tr>
            {open && (
                <tr className="bg-cm-surface-2">
                    <td colSpan={colSpan} className="px-3 pb-3">
                        <div className="overflow-x-auto rounded-cm-lg border border-cm-border bg-cm-surface">
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr>
                                        {detailHeaders.map((h) => (
                                            <th key={h} className={TH}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={detailHeaders.length} className="px-3 py-4 text-center text-cm-muted">
                                                No activity in this period
                                            </td>
                                        </tr>
                                    ) : (
                                        rows
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const DetailRow = ({ children }) => <tr className="border-t border-cm-border">{children}</tr>;
const DetailCell = ({ children, className = '' }) => (
    <td className={`px-3 py-2 whitespace-nowrap ${className}`}>{children}</td>
);

const TablePlayerReport = () => {
    const adminLogin = useSelector((state) => state.adminLogin) || {};
    const userLogin = useSelector((state) => state.userLogin) || {};
    const token = (adminLogin.userInfo || userLogin.userInfo)?.token;

    const [tab, setTab] = useState(0);
    const [playerTableId, setPlayerTableId] = useState('');
    const [loading, setLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [report, setReport] = useState(null);
    const [clubs, setClubs] = useState([]);
    const [filters, setFilters] = useState({
        clubId: '',
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // { table, runs } being invoiced; runs omitted = every run in the period
    const [invoiceTarget, setInvoiceTarget] = useState(null);
    // { player, sessions } being invoiced; sessions omitted = every session shown for the player
    const [playerInvoiceTarget, setPlayerInvoiceTarget] = useState(null);

    const fetchReport = useCallback(
        async ({ silent = false } = {}) => {
            if (!token) return;
            if (!silent) setLoading(true);
            try {
                const { data } = await axios.get(`${API_URL}/api/reports/table-player`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        startDate: filters.startDate,
                        endDate: filters.endDate,
                        ...(filters.clubId ? { clubId: filters.clubId } : {}),
                    },
                });
                setReport(data.data);
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to load table & player report');
            } finally {
                setLoading(false);
            }
        },
        [token, filters]
    );

    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API_URL}/api/reports/clubs`, { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => setClubs(data.data?.clubs || []))
            .catch(() => setClubs([]));
    }, [token]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    useEffect(() => {
        if (
            playerTableId &&
            report &&
            !report.tables.some((table) => String(table._id) === String(playerTableId))
        ) {
            setPlayerTableId('');
        }
    }, [report, playerTableId]);

    // Keep live figures (running tables, players still seated) current
    useEffect(() => {
        const id = setInterval(() => fetchReport({ silent: true }), REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchReport]);

    // Reset to the first page whenever the underlying row set changes
    useEffect(() => {
        setPage(0);
    }, [tab, filters, playerTableId]);

    const setFilter = (key) => (event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }));

    const summary = report?.summary;

    const tableFilteredPlayers = useMemo(() => {
        if (!report) return [];
        if (!playerTableId) return report.players;

        return report.players
            .map((player) => {
                const sessions = player.sessions.filter(
                    (session) => String(session.tableId) === String(playerTableId)
                );
                if (sessions.length === 0) return null;

                const activeSession = sessions.find((session) => session.status === 'Playing');
                return {
                    ...player,
                    sessionCount: sessions.length,
                    totalMinutes: sessions.reduce((sum, session) => sum + session.durationMinutes, 0),
                    totalBillableMinutes: sessions.reduce(
                        (sum, session) => sum + session.billableMinutes,
                        0
                    ),
                    totalCharge: sessions.reduce((sum, session) => sum + session.charge, 0),
                    paidAmount: sessions.reduce(
                        (sum, session) => sum + (session.paymentStatus === 'paid' ? session.charge : 0),
                        0
                    ),
                    unpaidAmount: sessions.reduce(
                        (sum, session) => sum + (session.paymentStatus === 'unpaid' ? session.charge : 0),
                        0
                    ),
                    currentTable: activeSession
                        ? `${activeSession.tableLabel} - Seat ${activeSession.seatNumber}`
                        : null,
                    sessions,
                };
            })
            .filter(Boolean)
            .sort((a, b) => b.totalMinutes - a.totalMinutes);
    }, [report, playerTableId]);

    const filteredPlayers = tableFilteredPlayers;

    const paginatedTables = useMemo(
        () => (report?.tables || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [report, page, rowsPerPage]
    );
    const paginatedPlayers = useMemo(
        () => filteredPlayers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredPlayers, page, rowsPerPage]
    );

    const exportRows = useMemo(() => {
        if (!report) return { columns: [], rows: [] };
        if (tab === 0) {
            return {
                columns: [
                    { title: 'Table', field: 'table' },
                    { title: 'Location', field: 'location' },
                    { title: 'Club', field: 'club' },
                    { title: 'Status', field: 'status' },
                    { title: 'Private Booking', field: 'privateBooking' },
                    { title: 'Running Since', field: 'runningSince' },
                    { title: 'Last Stopped', field: 'lastStoppedAt' },
                    { title: 'Active Time', field: 'activeTime' },
                    { title: 'Players Served', field: 'playersServed' },
                    { title: 'Total Amount (INR)', field: 'totalCharge' },
                ],
                rows: report.tables.map((t) => {
                    const latestRun = latestRunOf(t);
                    return {
                        table: `${t.tableName} (${t.tableNumber})`,
                        location: t.location,
                        club: t.club?.name || '',
                        status: t.status,
                        privateBooking: t.isPrivateNow
                            ? `Private until ${fmtTime(t.currentPrivateBooking?.until)}`
                            : t.privateBookings?.length
                              ? `Private (${t.privateBookings.length} in period)`
                              : '',
                        runningSince: fmtTime(latestRun?.startedAt || t.runningSince),
                        lastStoppedAt:
                            t.status === 'Running' ? '' : fmtTime(latestRun?.endedAt || t.lastStoppedAt),
                        activeTime: formatMinutes(t.activeMinutes),
                        playersServed: t.playersServed,
                        totalCharge: t.totalCharge,
                    };
                }),
            };
        }
        return {
            columns: [
                { title: 'Player', field: 'player' },
                { title: 'ID', field: 'identifier' },
                { title: 'Phone', field: 'phoneNumber' },
                { title: 'Now At', field: 'currentTable' },
                { title: 'Sessions', field: 'sessionCount' },
                { title: 'Time Played', field: 'timePlayed' },
                { title: 'Billed Time', field: 'billedTime' },
                { title: 'Total Amount (INR)', field: 'totalCharge' },
                { title: 'Paid (INR)', field: 'paidAmount' },
                { title: 'Unpaid (INR)', field: 'unpaidAmount' },
            ],
            rows: filteredPlayers.map((p) => ({
                player: p.name,
                identifier: p.identifier,
                phoneNumber: p.phoneNumber,
                currentTable: p.currentTable || '',
                sessionCount: p.sessionCount,
                timePlayed: formatMinutes(p.totalMinutes),
                billedTime: formatMinutes(p.totalBillableMinutes),
                totalCharge: p.totalCharge,
                paidAmount: p.paidAmount,
                unpaidAmount: p.unpaidAmount,
            })),
        };
    }, [report, tab, filteredPlayers]);

    const exportName = tab === 0 ? 'table_report' : 'player_report';
    const exportTitle = tab === 0 ? 'Table Report' : 'Player Report';

    const tableCount = report?.tables.length ?? 0;
    const totalRows = tab === 0 ? tableCount : filteredPlayers.length;
    const filterClub = clubs.find((club) => club._id === filters.clubId);
    const filterChips = [
        filters.clubId && { icon: Building2, label: filterClub?.name || 'Club' },
        (filters.startDate || filters.endDate) && { icon: CalendarDays, label: `${filters.startDate || '…'} → ${filters.endDate || '…'}` },
    ].filter(Boolean);

    return (
        <div className="flex flex-col gap-5 min-w-0">
            <ScreenHeader
                title="Player & table report"
                description="Time and money per table and per player. Live figures refresh every 30 seconds."
                actions={
                    <>
                        <FilterToggle
                            open={filtersOpen}
                            onToggle={() => setFiltersOpen((o) => !o)}
                            count={filterChips.length}
                            controls="table-report-filters"
                        />
                        <Button variant="secondary" icon={RefreshCw} loading={loading} onClick={() => fetchReport()}>
                            Refresh
                        </Button>
                        <Button
                            variant="secondary"
                            icon={FileSpreadsheet}
                            disabled={!report}
                            onClick={() => exportToExcel(exportRows.rows, exportRows.columns, exportName)}
                        >
                            Excel
                        </Button>
                        <Button
                            variant="secondary"
                            icon={FileDown}
                            disabled={!report}
                            onClick={() => exportToPDF(exportRows.rows, exportRows.columns, exportName, exportTitle)}
                        >
                            PDF
                        </Button>
                    </>
                }
            />

            {/* Filters */}
            <FilterCard
                id="table-report-filters"
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                chips={filterChips}
                emptyLabel="All clubs and dates"
            >
                <div className="grid gap-3 sm:grid-cols-3">
                    <SelectInput
                        label="Club"
                        value={filters.clubId}
                        onChange={setFilter('clubId')}
                        placeholder="All clubs"
                        options={clubs.map((club) => ({ value: club._id, label: club.name }))}
                    />
                    <TextInput label="From" type="date" value={filters.startDate} onChange={setFilter('startDate')} />
                    <TextInput label="To" type="date" value={filters.endDate} onChange={setFilter('endDate')} />
                </div>
            </FilterCard>

            <StatGrid>
                <StatCard
                    label="Tables running"
                    icon={Armchair}
                    tone="gold"
                    loading={loading && !summary}
                    value={summary ? `${summary.runningTables} / ${summary.totalTables}` : '—'}
                />
                <StatCard
                    label="Table active time"
                    icon={Clock}
                    loading={loading && !summary}
                    value={summary ? formatMinutes(summary.totalTableMinutes) : '—'}
                />
                <StatCard
                    label="Players"
                    icon={Users}
                    loading={loading && !summary}
                    value={summary ? summary.totalPlayers : '—'}
                    hint={summary ? `${summary.playersNow} seated now · ${formatMinutes(summary.totalPlayerMinutes)} played` : undefined}
                />
                <StatCard
                    label="Table charges"
                    icon={IndianRupee}
                    tone={summary?.unpaidAmount > 0 ? 'warning' : undefined}
                    loading={loading && !summary}
                    value={summary ? formatRupees(summary.totalCharge) : '—'}
                    hint={summary?.unpaidAmount > 0 ? `${formatRupees(summary.unpaidAmount)} unpaid` : undefined}
                />
            </StatGrid>

            {report?.truncated && (
                <Notice tone="warning" icon={AlertTriangle}>
                    Showing the most recent 5000 sessions. Narrow the date range for complete figures.
                </Notice>
            )}

            <Card padded={false}>
                <div className="flex flex-wrap items-center justify-between gap-3 p-3 sm:px-4 border-b border-cm-border">
                    <Tabs
                        items={[
                            { value: 0, label: 'Tables', icon: Armchair, count: tableCount },
                            { value: 1, label: 'Players', icon: Users, count: filteredPlayers.length },
                        ]}
                        value={tab}
                        onChange={setTab}
                        ariaLabel="Report view"
                    />
                    {tab === 1 && (
                        <div className="flex flex-wrap gap-2">
                            <SelectInput
                                size="sm"
                                className="w-full sm:w-64"
                                aria-label="Filter players by table"
                                value={playerTableId}
                                onChange={(event) => setPlayerTableId(event.target.value)}
                                placeholder="All tables"
                                options={(report?.tables || []).map((table) => ({
                                    value: table._id,
                                    label: `${table.tableName} (${table.tableNumber})${table.location ? ` · ${table.location}` : ''}`,
                                }))}
                            />
                        </div>
                    )}
                </div>

                {loading && !report ? (
                    <TableSkeleton rows={6} cols={6} />
                ) : totalRows === 0 ? (
                    <EmptyState
                        compact
                        icon={tab === 0 ? Armchair : Users}
                        title={tab === 0 ? 'No tables found' : 'No player activity in this period'}
                        description="Try another club or a wider date range."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        {tab === 0 ? (
                            <table className="w-full text-sm">
                                <thead className="bg-cm-surface-2">
                                    <tr>
                                        <th className={TH} aria-label="Details" />
                                        {['#', 'Table', 'Club / location', 'Status', 'Running since', 'Last stopped', 'Active time', 'Players', 'Amount', ''].map((h) => (
                                            <th key={h || 'invoice'} className={TH}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTables.map((row, idx) => {
                                        const latestRun = latestRunOf(row);
                                        const running = row.status === 'Running';
                                        return (
                                            <RowWithDetails
                                                key={row._id}
                                                colSpan={11}
                                                serialNumber={page * rowsPerPage + idx + 1}
                                                cells={
                                                    <>
                                                        <td className={TD}>
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                <span className="font-semibold text-cm-text">{row.tableName}</span>
                                                                {row.privateBookings?.length > 0 && (
                                                                    <PrivateChip booking={row.currentPrivateBooking || row.privateBookings[0]} />
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-cm-muted whitespace-nowrap">
                                                                {row.tableNumber} · {row.gameType} · {formatRupees(row.hourlyRate)}/hr
                                                            </div>
                                                            {row.isPrivateNow && (
                                                                <div className="text-xs font-semibold text-cm-warning">
                                                                    Private until {fmtTime(row.currentPrivateBooking?.until)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className={TD}>
                                                            <div>{row.club?.name || '—'}</div>
                                                            {row.location && <div className="text-xs text-cm-muted">{row.location}</div>}
                                                        </td>
                                                        <td className={TD}>
                                                            <StatusPill tone={running ? 'success' : 'neutral'} size="sm">
                                                                {running ? `Running · ${row.playersNow}` : 'Stopped'}
                                                            </StatusPill>
                                                        </td>
                                                        <td className={`${TD} whitespace-nowrap`}>{fmtTime(latestRun?.startedAt || row.runningSince)}</td>
                                                        <td className={`${TD} whitespace-nowrap`}>
                                                            {running ? <span className="text-cm-faint">—</span> : fmtTime(latestRun?.endedAt || row.lastStoppedAt)}
                                                        </td>
                                                        <td className={`${TD} tabular-nums whitespace-nowrap`}>{formatMinutes(row.activeMinutes)}</td>
                                                        <td className={`${TD} tabular-nums`}>{row.playersServed}</td>
                                                        <td className={`${TD} font-semibold tabular-nums whitespace-nowrap`}>{formatRupees(row.totalCharge)}</td>
                                                        <td className={TD}>
                                                            <InvoiceButton
                                                                disabled={running || row.runs.length === 0 || row.runs.some((run) => !run.endedAt)}
                                                                title={running ? 'Invoice is available after the table stops' : 'Invoice for all runs in this period'}
                                                                onClick={() => setInvoiceTarget({ table: row })}
                                                            />
                                                        </td>
                                                    </>
                                                }
                                                detailHeaders={['Started', 'Stopped', 'Duration', 'Peak players', 'Players served', 'Booking', 'Amount', '']}
                                                detailRows={() =>
                                                    row.runs.map((r) => (
                                                        <DetailRow key={r._id}>
                                                            <DetailCell>{fmtTime(r.startedAt)}</DetailCell>
                                                            <DetailCell>
                                                                {r.endedAt ? fmtTime(r.endedAt) : <StatusPill status="Running" tone="success" size="sm" />}
                                                            </DetailCell>
                                                            <DetailCell className="tabular-nums">{formatMinutes(r.activeMinutes)}</DetailCell>
                                                            <DetailCell className="tabular-nums">{r.peakPlayers}</DetailCell>
                                                            <DetailCell className="tabular-nums">{r.playersServed}</DetailCell>
                                                            <DetailCell>
                                                                {r.privateBooking ? <PrivateChip booking={r.privateBooking} /> : <Badge>Public</Badge>}
                                                            </DetailCell>
                                                            <DetailCell className="font-semibold tabular-nums">{formatRupees(r.totalCharge)}</DetailCell>
                                                            <DetailCell>
                                                                <InvoiceButton
                                                                    disabled={!r.endedAt}
                                                                    title={r.endedAt ? 'Invoice for this run' : 'Invoice is available after this run stops'}
                                                                    onClick={() => setInvoiceTarget({ table: row, runs: [r] })}
                                                                />
                                                            </DetailCell>
                                                        </DetailRow>
                                                    ))
                                                }
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-cm-surface-2">
                                    <tr>
                                        <th className={TH} aria-label="Details" />
                                        {['#', 'Player', 'Status', 'Sessions', 'Played', 'Billed time', 'Billed amount', ''].map((h) => (
                                            <th key={h || 'actions'} className={TH}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedPlayers.map((row, idx) => {
                                        const seated = isPlayerSeated(row);
                                        return (
                                            <RowWithDetails
                                                key={row._id}
                                                colSpan={9}
                                                serialNumber={page * rowsPerPage + idx + 1}
                                                cells={
                                                    <>
                                                        <td className={TD}>
                                                            <div className="font-semibold text-cm-text">{row.name}</div>
                                                            <div className="text-xs text-cm-muted whitespace-nowrap">
                                                                {[row.identifier, row.phoneNumber].filter(Boolean).join(' · ')}
                                                            </div>
                                                        </td>
                                                        <td className={TD}>
                                                            {row.currentTable ? (
                                                                <div className="flex flex-col items-start gap-0.5">
                                                                    <StatusPill status="Playing" tone="success" size="sm" />
                                                                    <span className="text-xs text-cm-muted whitespace-nowrap">{row.currentTable}</span>
                                                                </div>
                                                            ) : (
                                                                <StatusPill tone="neutral" size="sm">Left</StatusPill>
                                                            )}
                                                        </td>
                                                        <td className={`${TD} tabular-nums`}>{row.sessionCount}</td>
                                                        <td className={`${TD} tabular-nums whitespace-nowrap`}>{formatMinutes(row.totalMinutes)}</td>
                                                        <td className={`${TD} tabular-nums whitespace-nowrap`}>{formatMinutes(row.totalBillableMinutes)}</td>
                                                        <td className={`${TD} font-semibold tabular-nums whitespace-nowrap`}>{formatRupees(row.totalCharge)}</td>
                                                        <td className={TD}>
                                                            <div className="flex items-center gap-1">
                                                                <InvoiceButton
                                                                    disabled={seated || row.sessions.length === 0}
                                                                    title={seated
                                                                        ? 'Invoice is available after the player leaves the table'
                                                                        : 'Invoice for all sessions in this period'}
                                                                    onClick={() => setPlayerInvoiceTarget({ player: row })}
                                                                />
                                                            </div>
                                                        </td>
                                                    </>
                                                }
                                                detailHeaders={['Table / seat', 'Sat down', 'Left', 'Played', 'Billed time', 'Rate', 'Charge', 'Payment', '']}
                                                detailRows={() =>
                                                    row.sessions.map((s) => (
                                                        <DetailRow key={s._id}>
                                                            <DetailCell>{s.tableLabel} · Seat {s.seatNumber}</DetailCell>
                                                            <DetailCell>{fmtTime(s.startedAt)}</DetailCell>
                                                            <DetailCell>
                                                                {s.endedAt ? fmtTime(s.endedAt) : <StatusPill status="Playing" tone="success" size="sm" />}
                                                            </DetailCell>
                                                            <DetailCell className="tabular-nums">{formatMinutes(s.durationMinutes)}</DetailCell>
                                                            <DetailCell className="tabular-nums">{formatMinutes(s.billableMinutes)}</DetailCell>
                                                            <DetailCell className="tabular-nums">{formatRupees(s.hourlyRate)}/hr</DetailCell>
                                                            <DetailCell className="font-semibold tabular-nums">{formatRupees(s.charge)}</DetailCell>
                                                            <DetailCell><PaymentChip status={s.paymentStatus} method={s.paymentMethod} markedUnpaid={Boolean(s.markedUnpaidAt)} /></DetailCell>
                                                            <DetailCell>
                                                                <InvoiceButton
                                                                    disabled={seated}
                                                                    title={seated
                                                                        ? 'Invoice is available after the player leaves the table'
                                                                        : 'Invoice for this session'}
                                                                    onClick={() => setPlayerInvoiceTarget({ player: row, sessions: [s] })}
                                                                />
                                                            </DetailCell>
                                                        </DetailRow>
                                                    ))
                                                }
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {report && totalRows > 0 && (
                    <Pagination
                        page={page}
                        pageSize={rowsPerPage}
                        total={totalRows}
                        pageSizeOptions={[10, 25, 50, 100]}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => {
                            setRowsPerPage(size);
                            setPage(0);
                        }}
                    />
                )}
            </Card>

            <TableInvoiceDialog
                open={Boolean(invoiceTarget)}
                onClose={() => setInvoiceTarget(null)}
                table={invoiceTarget?.table}
                runs={invoiceTarget?.runs}
                token={token}
            />
            <PlayerInvoiceDialog
                open={Boolean(playerInvoiceTarget)}
                onClose={() => setPlayerInvoiceTarget(null)}
                player={playerInvoiceTarget?.player}
                sessions={playerInvoiceTarget?.sessions}
                tables={report?.tables}
                token={token}
            />
        </div>
    );
};

export default TablePlayerReport;
